import React, { useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import FacturaPreview, { printFactura } from './FacturaPreview';
import { shareFacturaPdf } from '../utils/facturaPdf';

/**
 * Vista previa del pedido.
 * - Guardar: solo en tablet (no va a oficina/admin).
 * - Enviar: crea el pedido en el servidor → oficina + admin.
 * - WhatsApp: comparte el PDF (menú del teléfono).
 *
 * @param {object|null} success — snapshot after Enviar: { pedido, cliente, items, pedidoMeta, subtotal, offline }
 */
export default function OrderConfirmModal({
  open,
  onClose,
  onGuardarLocal,
  onEnviarOficina,
  onDone,
  sending,
  offline = false,
  error = '',
  success = null,
}) {
  const cart = useCart();
  const [sharing, setSharing] = useState(false);
  const [shareHint, setShareHint] = useState('');
  const isSuccess = Boolean(success);
  const cliente = isSuccess ? success.cliente : cart.state.cliente;
  const items = isSuccess ? success.items : cart.state.items;
  const pedidoMeta = isSuccess ? success.pedidoMeta : cart.state.pedidoMeta;
  const subtotal = isSuccess ? success.subtotal : cart.subtotal;
  const pedido = success?.pedido || {};
  const numero = pedido.numero || null;

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        if (isSuccess) onDone?.();
        else onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onDone, isSuccess]);

  useEffect(() => {
    if (!open) {
      setShareHint('');
      setSharing(false);
    }
  }, [open]);

  if (!open) return null;

  function handleDismiss() {
    if (isSuccess) onDone?.();
    else onClose();
  }

  async function handleWhatsAppPdf() {
    setShareHint('');
    setSharing(true);
    try {
      const snap = success;
      const result = await shareFacturaPdf({
        telefono: (snap?.cliente || cliente)?.telefono,
        clienteTelefono: (snap?.cliente || cliente)?.telefono,
        numero: snap?.pedido?.numero || numero,
        clienteNombre: (snap?.cliente || cliente)?.nombre,
        items: snap?.items || items,
        subtotal: snap?.subtotal ?? subtotal,
        offline: Boolean(snap?.offline),
      });
      if (result?.via === 'download+wa-text') {
        setShareHint('PDF descargado. Adjúntalo en el chat de WhatsApp.');
      }
    } catch (err) {
      setShareHint(err?.message || 'No se pudo compartir el PDF');
    } finally {
      setSharing(false);
    }
  }

  const busy = sending || sharing;
  const enviarLabel = sending
    ? offline
      ? 'Guardando para enviar…'
      : 'Enviando…'
    : 'Enviar a oficina';
  const guardarLabel = 'Guardar en tablet';

  return (
    <div className="modal-overlay" onClick={handleDismiss} role="presentation">
      <div
        className="modal order-confirm factura-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isSuccess ? 'Pedido enviado' : 'Vista previa del pedido'}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal-close-v2" onClick={handleDismiss} aria-label="Cerrar">
          ✕
        </button>
        {isSuccess ? (
          <div className="order-confirm-success-banner" role="status">
            <strong>
              {success.offline
                ? success.entry?.hold_until_manual
                  ? 'Pedido guardado en tablet'
                  : 'Pedido listo en la tablet'
                : `Pedido ${numero || ''} enviado a oficina`}
            </strong>
            <span>
              {success.offline
                ? success.entry?.hold_until_manual
                  ? 'Carrito libre para otro pedido. Ábrelo en Guardados; envía a oficina cuando confirmen la cotización.'
                  : 'Se enviará al volver internet. Comparte el PDF por WhatsApp si quieres.'
                : 'Ya lo ven oficina y admin. Puedes imprimir o enviar el PDF por WhatsApp.'}
            </span>
          </div>
        ) : (
          <p className="order-confirm-hint">
            <strong>Guardar en tablet</strong> = lo guarda aquí, vacía el carrito y puedes tomar otro;
            después lo envías a oficina con «Enviar ahora».{' '}
            <strong>Enviar a oficina</strong> = lo manda ya (sin PDF).{' '}
            <strong>WhatsApp (PDF)</strong> = comparte el PDF.
          </p>
        )}
        <div className="order-confirm-scroll" id="factura-print-area">
          <FacturaPreview
            titulo={isSuccess ? (success.offline ? 'Pedido (sin enviar aún)' : 'Pedido enviado') : 'Vista previa del pedido'}
            numero={numero}
            cliente={cliente}
            pedidoMeta={pedidoMeta}
            pedido={pedido}
            items={items}
            subtotal={subtotal}
          />
        </div>
        <div className="order-confirm-foot factura-modal-foot">
          {error && !isSuccess ? <p className="cart-error">{error}</p> : null}
          {shareHint ? <p className="cart-error">{shareHint}</p> : null}
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => printFactura('factura-print-area')}
          >
            Imprimir / PDF
          </button>
          <button
            type="button"
            className="btn-whatsapp"
            disabled={busy}
            onClick={handleWhatsAppPdf}
          >
            {sharing ? 'Preparando PDF…' : 'WhatsApp (PDF)'}
          </button>
          {isSuccess ? (
            <button type="button" className="btn-primary" onClick={handleDismiss}>
              Listo
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={onGuardarLocal}
              >
                {guardarLabel}
              </button>
              <button
                type="button"
                className="btn-primary btn-lg"
                disabled={busy}
                onClick={onEnviarOficina}
              >
                {enviarLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
